import { ChevronDown, ChevronRight, ExternalLink, RefreshCw, Trophy } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { toast } from 'sonner'

import {
  useCalibrations,
  useRankings,
  useStage,
  useTriggerCalibration,
  type RankingListItemOut,
  type CalibrationEntryOut,
  type StageOut,
} from '@/api/endpoints/ranking'
import { Button } from '@/components/ui/Button'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { Pagination } from '@/components/ui/Pagination'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { ScoreGauge } from '@/components/ds/ScoreGauge'
import { RankingBreakdownView } from '@/features/ranking/components/RankingBreakdownView'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { preservingChannel } from '@/lib/routing'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import styles from './RankingPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const PAGE_LIMIT = 100

/**
 * Channel-wide ranked backlog. Two columns:
 *   - Left: ranked rows from ``/rankings`` (filterable by factory).
 *   - Right: stage card (warmup → first calibration → steady) + manual
 *     "Recalibrar agora" trigger.
 *
 * Quarantined videos render with a danger chip; everything else maps to
 * "elegível" since the upload tracker isn't joined here. To pivot to a
 * publication view, link to `/projects/{id}` from each row.
 */
export function RankingPage() {
  const { active, channels, isLoading: channelsLoading } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const rankings = useRankings(channel, PAGE_LIMIT)
  const stage = useStage(channel)
  const calibrations = useCalibrations(channel, 10)
  const triggerCal = useTriggerCalibration()
  const navigate = useNavigate()

  // Pagination + filter live in the URL (`?page=&size=&factory=`) so
  // refresh / back-forward / shareable links all preserve state. The
  // route's validateSearch sanitizes raw values; we just read them and
  // call navigate to update.
  const search = useSearch({ from: '/_app/ranking' })
  const factoryFilter = search.factory ?? null
  const page = search.page ?? 1
  const pageSize = search.size ?? 25
  const [calibrationResult, setCalibrationResult] = useState<CalibrationResult | null>(null)
  // Animate row reorder + filter switch — when the user toggles factory
  // filter or paginates, items shift smoothly instead of popping.
  const [tableBodyRef] = useAutoAnimate<HTMLDivElement>()

  function patchSearch(patch: Partial<typeof search>): void {
    void navigate({
      to: '/ranking',
      search: (prev) => {
        const next = { ...prev, ...patch }
        // Drop default values so the URL stays clean (no `?page=1&size=25`).
        if (next.page === 1) delete next.page
        if (next.size === 25) delete next.size
        if (!next.factory) delete next.factory
        return next
      },
    })
  }

  const factories = useMemo(() => {
    const set = new Set<string>()
    for (const r of rankings.data ?? []) set.add(r.factory)
    return Array.from(set).sort()
  }, [rankings.data])

  const visible = useMemo(() => {
    const all = rankings.data ?? []
    if (factoryFilter === null) return all
    return all.filter((r) => r.factory === factoryFilter)
  }, [rankings.data, factoryFilter])

  const pageRows = useMemo(
    () => visible.slice((page - 1) * pageSize, page * pageSize),
    [visible, page, pageSize],
  )

  const channelAvg = useMemo(() => {
    const all = rankings.data ?? []
    const scored = all.filter((r): r is RankingListItemOut & { final_score: number } =>
      typeof r.final_score === 'number',
    )
    if (scored.length === 0) return null
    const sum = scored.reduce((acc, r) => acc + r.final_score, 0)
    return sum / scored.length
  }, [rankings.data])

  if (channelsLoading) {
    return (
      <div className={styles.page}>
        <Header />
        <p>Carregando...</p>
      </div>
    )
  }
  if (channels.length === 0 || !channel) {
    return (
      <div className={styles.page}>
        <Header />
        <EmptyState title="Sem canal selecionado" description="Escolha um canal no sidebar." />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Header channel={active?.name ?? channel} />

      <div className={styles.layout}>
        <div className={styles.left}>
          <div className={styles.controls}>
            <FactoryFilter
              factories={factories}
              value={factoryFilter}
              onChange={(next) => {
                // Reset paginator so we don't land on an empty page after
                // narrowing the result set.
                // Casted because exactOptionalPropertyTypes refuses
                // ``factory: undefined``; patchSearch's own ``delete``
                // pass strips the key when truthy-falsy, preserving the
                // "clear filter" semantic.
                patchSearch({ factory: next ?? undefined, page: 1 } as Partial<typeof search>)
              }}
            />
            <div className={styles.spacer} />
            <span className={styles.countPill}>
              {visible.length}{' '}
              {visible.length === 1 ? 'vídeo' : 'vídeos'}
              {factoryFilter ? ` · ${factoryFilter}` : ''}
            </span>
          </div>

          {rankings.isPending ? (
            <RankingTableSkeleton rows={8} />
          ) : rankings.isError ? (
            <QueryErrorNotice
              error={rankings.error}
              label="os rankings"
              onRetry={() => {
                void rankings.refetch()
              }}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<Trophy />}
              title="Sem rankings"
              description="Nenhum vídeo desse canal foi ranqueado ainda."
            />
          ) : (
            <>
              <div className={styles.table}>
                <div className={cn(styles.row, styles.headRow)}>
                  <span aria-hidden />
                  <span>Score</span>
                  <span>Vídeo</span>
                  <span>Views</span>
                  <span>Lang</span>
                  <span>Factory</span>
                  <span>Status</span>
                </div>
                <div ref={tableBodyRef}>
                  {pageRows.map((r) => (
                    <RankingRow
                      key={`${r.video_id}-${r.language}`}
                      ranking={r}
                      onOpenProject={() => {
                        void navigate({
                          to: '/projects/$projectId',
                          params: { projectId: r.video_id },
                          search: preservingChannel({ lang: r.language }),
                        })
                      }}
                    />
                  ))}
                </div>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                total={visible.length}
                onPageChange={(next) => {
                  patchSearch({ page: next })
                }}
                onPageSizeChange={(next) => {
                  patchSearch({ size: next, page: 1 })
                }}
                itemLabel="vídeo"
                itemLabelPlural="vídeos"
              />
            </>
          )}
        </div>

        <aside className={styles.sideRail}>
          <StageCard
            stage={stage.data}
            channelName={active?.name ?? channel}
            videoCount={rankings.data?.length ?? 0}
            channelAvg={channelAvg}
            calibrationsCount={calibrations.data?.length ?? 0}
          />
          <StageTimeline stage={stage.data} />
          <CalibrateCard
            running={triggerCal.isPending}
            onTrigger={(force) => {
              triggerCal.mutate(
                { channel, force },
                {
                  onSuccess: (envelope) => {
                    const result = parseCalibrationResult(envelope)
                    setCalibrationResult(result)
                    notifyCalibration(result)
                  },
                  onError: (err) => {
                    toast.error('Falha ao calibrar', {
                      description: errorMessage(err, 'Veja o request_id no log do backend.'),
                    })
                  },
                },
              )
            }}
            lastResult={calibrationResult}
            error={triggerCal.error}
          />
          <CalibrationHistoryCard
            entries={calibrations.data ?? []}
            loading={calibrations.isPending}
          />
        </aside>
      </div>
    </div>
  )
}

function Header({ channel }: { channel?: string } = {}) {
  return (
    <PageHeader
      eyebrow="Sistema de Ranking"
      title="Backlog ranqueado"
      description={
        channel
          ? `Pontuação 0–1 por vídeo combinando regras fixas e juiz de IA · ${channel}. O runner publica nesta ordem.`
          : 'Pontuação 0–1 por vídeo combinando regras fixas e juiz de IA. O runner publica nesta ordem.'
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

/**
 * Loading state for the rankings table. Mirrors the real ``.row`` grid so
 * the layout doesn't reflow when data lands. ``rows`` controls how many
 * placeholders to draw — typically the page's expected first page size.
 */
function RankingTableSkeleton({ rows }: { rows: number }) {
  return (
    <div className={styles.table}>
      <div className={cn(styles.row, styles.headRow)}>
        <span aria-hidden />
        <span>Score</span>
        <span>Vídeo</span>
        <span>Views</span>
        <span>Lang</span>
        <span>Factory</span>
        <span>Status</span>
      </div>
      {Array.from({ length: rows }).map((_, idx) => (
        <div key={`sk-${String(idx)}`} className={styles.row} aria-hidden>
          <span />
          <div className={cn(styles.skeletonShimmer, styles.skeletonGauge)} />
          <div className={styles.skeletonTitleStack}>
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine, styles.skeletonLineWide)} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonLine, styles.skeletonLineNarrow)} />
          </div>
          <div className={cn(styles.skeletonShimmer, styles.skeletonLine, styles.skeletonLineNarrow)} />
          <div className={cn(styles.skeletonShimmer, styles.skeletonChip)} />
          <div className={cn(styles.skeletonShimmer, styles.skeletonChip)} />
          <div className={cn(styles.skeletonShimmer, styles.skeletonChip)} />
        </div>
      ))}
    </div>
  )
}

interface RankingRowProps {
  ranking: RankingListItemOut
  /** Navigates to the full project page — wired to a side button so the
   *  row click itself can be reserved for expanding the safety detail. */
  onOpenProject: () => void
}

function RankingRow({ ranking, onOpenProject }: RankingRowProps) {
  const [open, setOpen] = useState(false)
  const guardrails = ranking.guardrails_violated
  const Caret = open ? ChevronDown : ChevronRight

  return (
    <div className={styles.rowWrap}>
      <button
        type="button"
        className={cn(styles.row, styles.dataRow)}
        aria-expanded={open}
        onClick={() => { setOpen((prev) => !prev) }}
      >
        <span className={styles.caretCell} aria-hidden>
          <Caret size={16} />
        </span>
        <ScoreGauge score={ranking.final_score} size={46} />
        <div className={styles.titleCell}>
          <div className={styles.title}>{ranking.title || ranking.video_id}</div>
          {guardrails.length > 0 ? (
            <div className={styles.guardrailsLine}>
              {guardrails.length === 1
                ? `Guardrail: ${guardrails[0] ?? ''}`
                : `${String(guardrails.length)} guardrails violados`}
            </div>
          ) : null}
        </div>
        <span className={cn(styles.viewsCell, styles.viewsCellEmpty)}>—</span>
        <Chip>{ranking.language}</Chip>
        <Chip>{ranking.factory}</Chip>
        {ranking.quarantined ? (
          <Chip variant="danger">Bloqueado</Chip>
        ) : (
          <Chip variant="success">Elegível</Chip>
        )}
      </button>
      {open ? (
        <div className={styles.rowDetail}>
          <div className={styles.rowDetailHeader}>
            <span className={styles.rowDetailTitle}>Detalhamento do ranking</span>
            <Button
              size="sm"
              variant="ghost"
              trailingIcon={<ExternalLink />}
              onClick={onOpenProject}
            >
              Abrir projeto
            </Button>
          </div>
          <RankingBreakdownView ranking={ranking} inline />
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Factory filter
// ---------------------------------------------------------------------------

interface FactoryFilterProps {
  factories: string[]
  value: string | null
  onChange: (next: string | null) => void
}

function FactoryFilter({ factories, value, onChange }: FactoryFilterProps) {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" trailingIcon={<ChevronDown />}>
          Factory · {value ?? 'Todas'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Filtro</DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() => {
            onChange(null)
          }}
        >
          Todas
        </DropdownMenuItem>
        {factories.length > 0 ? <DropdownMenuSeparator /> : null}
        {factories.map((f) => (
          <DropdownMenuItem
            key={f}
            onSelect={() => {
              onChange(f)
            }}
          >
            {f}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}

// ---------------------------------------------------------------------------
// Side rail
// ---------------------------------------------------------------------------

interface StageCardProps {
  stage: StageOut | undefined
  channelName: string
  videoCount: number
  channelAvg: number | null
  calibrationsCount: number
}

function StageCard({
  stage,
  channelName,
  videoCount,
  channelAvg,
  calibrationsCount,
}: StageCardProps) {
  const stageLabel = stageLabelOf(stage?.stage)
  const isWarmup = (stage?.stage ?? 'warmup') === 'warmup'
  const stableTarget = 30
  const stableCount = stage?.stable_signal_count ?? 0
  const daysToEligible = stage?.days_until_eligible ?? null

  return (
    <div className={styles.railCard}>
      <h3 className={styles.railTitle}>
        <span className={cn(styles.stageDot, isWarmup && styles.stageDotWarmup)} aria-hidden />
        Estágio atual · {stageLabel}
      </h3>
      <div className={styles.railAvg}>
        <ScoreGauge score={channelAvg} size={58} />
        <div>
          <div className={styles.railAvgLabel}>Média do canal</div>
          <div className={styles.railAvgChannel}>{channelName}</div>
          <div className={styles.railAvgSubtext}>
            {videoCount} vídeo{videoCount === 1 ? '' : 's'}
            {stage?.last_calibration_at
              ? ` · recalc ${formatRelative(stage.last_calibration_at)}`
              : ''}
          </div>
        </div>
      </div>
      <div className={styles.railMetrics}>
        <div>
          <div className={styles.railMetricKey}>Estáveis</div>
          <div className={styles.railMetricValue}>
            {stableCount}
            <small>/{stableTarget}</small>
          </div>
        </div>
        <div>
          <div className={styles.railMetricKey}>Calibrações</div>
          <div className={styles.railMetricValue}>{calibrationsCount}</div>
        </div>
        <div>
          <div className={styles.railMetricKey}>Próx. janela</div>
          <div className={styles.railMetricValue}>
            {daysToEligible === null ? '—' : `${String(daysToEligible)}d`}
          </div>
        </div>
      </div>
    </div>
  )
}

function StageTimeline({ stage }: { stage: StageOut | undefined }) {
  const current = stage?.stage ?? 'warmup'
  const stages: { key: string; label: string; description: string }[] = [
    {
      key: 'warmup',
      label: 'Aquecimento',
      description: 'Ativo desde o dia 1 · juiz observacional, gate desligado.',
    },
    {
      key: 'first_calibration',
      label: 'Primeira calibração',
      description: '30+ vídeos estáveis 150d · pesos ajustados · juiz pode auto-ativar.',
    },
    {
      key: 'steady',
      label: 'Regime permanente',
      description: 'Recalibra a cada 30d · gate em penúmbra 20% · rollback automático.',
    },
  ]

  return (
    <div className={styles.railCard}>
      <h3 className={styles.railTitle}>Como o ranking evolui</h3>
      <div className={styles.stages}>
        {stages.map((s, i) => {
          const isActive = stageMatches(current, s.key)
          return (
            <div key={s.key} className={cn(styles.stage, isActive && styles.stageActive)}>
              <span className={styles.stageCirc}>{String(i + 1).padStart(2, '0')}</span>
              <div className={styles.stageBody}>
                <div className={styles.stageHead}>{s.label}</div>
                <p className={styles.stageDescription}>{s.description}</p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface CalibrateCardProps {
  running: boolean
  onTrigger: (force: boolean) => void
  /** Last calibration response in this session — shown inline so the user
   * doesn't have to chase the toast. ``null`` means we haven't fired yet. */
  lastResult: CalibrationResult | null
  error: unknown
}

function CalibrateCard({ running, onTrigger, lastResult, error }: CalibrateCardProps) {
  return (
    <div className={styles.railCard}>
      <h3 className={styles.railTitle}>Recalibrar agora</h3>
      <p className={styles.stageDescription}>
        Roda a calibração mensal manualmente. Útil quando o critério mudou ou um lote grande de
        vídeos novos chegou.
      </p>
      <div className={styles.calibrateCta}>
        <Button
          size="sm"
          leadingIcon={<RefreshCw />}
          disabled={running}
          onClick={() => {
            onTrigger(false)
          }}
        >
          {running ? 'Calibrando...' : 'Calibrar'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={running}
          onClick={() => {
            onTrigger(true)
          }}
        >
          Forçar (ignorar guard)
        </Button>
        {lastResult ? (
          <div className={styles.calibrateResult}>
            <Chip variant={chipVariantForCalibration(lastResult.status)}>{lastResult.status}</Chip>
            {lastResult.summary ? (
              <span className={styles.stageDescription}>{lastResult.summary}</span>
            ) : null}
          </div>
        ) : null}
        {error && !lastResult ? (
          <span className={styles.stageDescription}>{errorMessage(error)}</span>
        ) : null}
      </div>
    </div>
  )
}

interface CalibrationHistoryCardProps {
  entries: CalibrationEntryOut[]
  loading: boolean
}

/**
 * Recent calibration runs from /rankings/calibrations. Each entry shows
 * the version transition (v1 → v2), who triggered it, sample size, the
 * action list (e.g. "weights_adjusted", "judge_promoted"), and a danger
 * chip when a rollback was applied.
 *
 * Renders even when empty so users see *that* the section exists — the
 * empty state explains the rule of thumb (calibrate ~30d apart).
 */
function CalibrationHistoryCard({ entries, loading }: CalibrationHistoryCardProps) {
  return (
    <div className={styles.railCard}>
      <h3 className={styles.railTitle}>Histórico de calibrações</h3>
      {loading ? (
        <p className={styles.stageDescription}>Carregando...</p>
      ) : entries.length === 0 ? (
        <p className={styles.stageDescription}>
          Nenhuma calibração ainda. Vai rodar automaticamente a cada ~30 dias quando o canal sair do
          aquecimento.
        </p>
      ) : (
        <ol className={styles.calibrationList}>
          {entries.map((c) => (
            <li key={c.id} className={styles.calibrationItem}>
              <div className={styles.calibrationHead}>
                <span className={styles.calibrationVersion}>
                  {c.criteria_version_before}{' → '}{c.criteria_version_after}
                </span>
                <span className={styles.calibrationWhen}>{formatCalibrationWhen(c.triggered_at)}</span>
              </div>
              <div className={styles.calibrationMeta}>
                <Chip variant={c.triggered_by === 'manual' ? 'info' : 'default'}>{c.triggered_by}</Chip>
                {c.rollback_triggered ? <Chip variant="danger">rollback</Chip> : null}
                <span className={styles.stageDescription}>
                  {c.n_videos_used} amostra{c.n_videos_used === 1 ? '' : 's'} · {c.stage}
                </span>
              </div>
              {c.actions.length > 0 ? (
                <div className={styles.calibrationActions}>
                  {c.actions.map((a) => (
                    <Chip key={a}>{a}</Chip>
                  ))}
                </div>
              ) : null}
              {c.notes ? <p className={styles.calibrationNotes}>{c.notes}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCalibrationWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function stageLabelOf(stage: string | undefined): string {
  switch (stage) {
    case 'warmup':
      return 'Aquecimento'
    case 'first_calibration':
      return 'Primeira calibração'
    case 'steady':
      return 'Regime permanente'
    default:
      return stage ?? '—'
  }
}

/**
 * The backend may use a richer set of stage strings than the UI renders
 * (``warmup_done``, ``calibrating``...). Map sensibly so unrecognised
 * values don't leave every step inactive.
 */
function stageMatches(current: string, key: string): boolean {
  if (current === key) return true
  if (key === 'warmup' && current === 'warmup_done') return true
  if (key === 'first_calibration' && current === 'calibrating') return true
  return false
}

// ---------------------------------------------------------------------------
// Calibration response handling
// ---------------------------------------------------------------------------

/**
 * Outcome of POST /rankings/calibrate. The backend returns one of several
 * shapes depending on what happened — we collapse them into a friendly
 * tuple so the UI stays simple.
 */
export interface CalibrationResult {
  status: string
  summary: string
  ok: boolean
}

function parseCalibrationResult(envelope: unknown): CalibrationResult {
  const data = (envelope as { data?: Record<string, unknown> } | null)?.data
  if (!data || typeof data !== 'object') {
    return { status: 'unknown', summary: '', ok: false }
  }
  const raw = data
  const status = typeof raw['status'] === 'string' ? raw['status'] : 'done'
  const reason = typeof raw['reason'] === 'string' ? raw['reason'] : ''
  const have = typeof raw['have'] === 'number' ? raw['have'] : null
  const need = typeof raw['need_for_kfold'] === 'number' ? raw['need_for_kfold'] : null
  const actions = Array.isArray(raw['actions']) ? raw['actions'] : null

  // Compose a one-liner that matches the actual outcome.
  let summary = ''
  if (status === 'skipped' && reason.startsWith('insufficient_samples')) {
    summary =
      have !== null && need !== null
        ? `Amostras insuficientes (${String(have)}/${String(need)})`
        : have !== null
          ? `Apenas ${String(have)} amostras válidas`
          : 'Amostras insuficientes'
  } else if (status === 'skipped' && reason === 'too_recent') {
    summary = 'Calibrou há pouco tempo. Use "Forçar" pra ignorar o guard.'
  } else if (status === 'skipped') {
    summary = reason.replace(/_/g, ' ') || 'Calibração ignorada'
  } else if (actions && actions.length > 0) {
    summary = `${String(actions.length)} ação(ões): ${actions.join(', ')}`
  } else {
    summary = reason.replace(/_/g, ' ')
  }

  const ok = status !== 'skipped' && status !== 'failed' && status !== 'unknown'
  return { status, summary, ok }
}

function chipVariantForCalibration(status: string): ChipVariant {
  if (status === 'skipped') return 'warning'
  if (status === 'failed' || status === 'unknown') return 'danger'
  return 'success'
}

function notifyCalibration(result: CalibrationResult) {
  const description = result.summary || undefined
  if (result.ok) {
    toast.success(`Calibração: ${result.status}`, description ? { description } : undefined)
    return
  }
  if (result.status === 'skipped') {
    toast.warning(`Calibração ignorada`, description ? { description } : undefined)
    return
  }
  toast.error(`Calibração: ${result.status}`, description ? { description } : undefined)
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000)
  if (days < 1) return 'hoje'
  if (days === 1) return '1d'
  return `${String(days)}d`
}
